/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-constant-condition */
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { FormEventHandler, useEffect, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { ErrorMessage } from "@hookform/error-message";

import { IUserRegisterForm, IRegisterValidationResponse } from "../types/forms";
import api from "../services/api";
import Translate from "../components/Translate";
import useAppTranslations from "../hooks/useAppTranslations";
import useYupFormResolver from "../hooks/useYupFormResolver";
import FormTextField from "../components/FormTextField";
import Landing from "../components/Landing";
import HashValidationWrapper from "../components/HashValidationWrapper";
import TermsOfUse from "../components/TermsOfUse";

import { phoneMask } from "../helpers/mask";
import regex from "../helpers/regex";
import FormTextFieldPassword from "../components/FormTextFieldPassword";

enum RegistrationType {
  Company = 1,
  User = 2
}

const formInitialState: IUserRegisterForm = {
  termsAccepted: false,
  companyName: "",
  companyCnpj: "",
  companyId: 0,
  creatorId: 0,
  userEmail: "",
  userName: "",
  userPhone: "",
  password: "",
  confirmPassword: "",
  planId: 0,
  payment: null,
  affiliationCode: null
};

export default function UserRegisterPage() {
  const { t } = useAppTranslations();
  const [registrationType, setRegistrationType] = useState<RegistrationType>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const extractAffiliationCode = () => {
    const params = new URLSearchParams(location.search);
    return params.get("affiliationCode");
  };

  const yupFormValidation = yup.object({
    userEmail: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:corp_email")
        })
      )
      .email(t("validation:valid_email")),

    userName: yup.string().required(
      t("validation:required_field", {
        fieldName: t("form:field:full_name")
      })
    ),

    userPhone: yup
      .string()
      .matches(regex.PHONE_NUMBER, t("validation:valid_phone"))
      .notRequired(),

    password: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:password")
        })
      )
      .min(8, t("validation:password_min_chars", { count: 8 }))
      .matches(regex.INCLUDE_NUMBER_CHAR, t("validation:password_number"))
      .matches(regex.INCLUDE_UPPER_CHAR, t("validation:password_min_uppercase_letter"))
      .matches(regex.INCLUDE_SPECIAL_CHAR, t("validation:password_reserved_char")),

    confirmPassword: yup
      .string()
      .required(
        t("validation:required_field", {
          fieldName: t("form:field:password_confirm")
        })
      )
      .oneOf([yup.ref("password")], t("validation:valid_confirm_password")),

    termsAccepted: yup.boolean().equals([true], t("validation:valid_terms_of_use"))
  });

  const yupFormResolver = useYupFormResolver<typeof yupFormValidation>(yupFormValidation);

  const formHook = useForm<IUserRegisterForm>({
    resolver: yupFormResolver,
    values: formInitialState,
    mode: "onBlur"
  });

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = ev => {
    if (!formHook.watch("termsAccepted")) {
      formHook.setError("termsAccepted", {
        type: "required",
        message: t("validation:valid_terms_of_use")
      });
    } else
      formHook.handleSubmit(data => {
        data.affiliationCode = extractAffiliationCode();
        if (registrationType === RegistrationType.Company) {
          if (data.affiliationCode === null) {
            navigate(`/charge`, {
              state: { data }
            });
          } else {
            const path = {
              pathname: "/charge",
              search: createSearchParams({ affiliationCode: data.affiliationCode }).toString()
            };
            navigate(path, {
              state: { data }
            });
          }
        } else if (registrationType === RegistrationType.User) {
          setLoading(true);
          // remove masks;
          data.companyCnpj = data.companyCnpj.replaceAll(/[^0-9]/gi, "");
          data.userPhone = data.userPhone.replaceAll(/[^0-9]/gi, "");
          api.postRegisterUser(data).then(v => {
            if (v.data.success) {
              setLoading(false);
              navigate("/thanks");
            } else {
              setLoading(false);
            }
          });
        }
      })(ev);
  };

  const handleRegisterValidation = (response: IRegisterValidationResponse) => {
    formHook.setValue("userEmail", response.userEmail);
    formHook.setValue("userName", response.userName);
    formHook.setValue("companyName", response.companyName);
    formHook.setValue("companyCnpj", response.companyCnpj);
    formHook.setValue("companyId", response.companyId);
    formHook.setValue("creatorId", response.creatorId);
    formHook.setValue("planId", response.planId);
    setRegistrationType(response.type);
  };

  const companyName = formHook.watch("companyName");

  return (
    <HashValidationWrapper onValidateHash={handleRegisterValidation}>
      <Landing
        headerTitle={t("title:join_company_team")}
        submitButtonLabel={t("action:proceed_registration")}
        handleCardFormSubmit={handleFormSubmit}
        submitLoading={loading}
        CardFormContent={
          <>
            {companyName && (
              <Box className="mb-6">
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: theme => theme.palette.text.primary
                  }}
                >
                  {companyName}
                </Typography>
              </Box>
            )}

            <Box
              className="rounded-2xl"
              sx={theme => ({
                py: 3,
                px: 4,
                background: theme.palette.background.default,
                border: `solid 1px ${theme.palette.grey[300]}`,

                [theme.breakpoints.down("sm")]: {
                  border: "none",
                  py: 1.5,
                  px: 2
                }
              })}
            >
              <Stack
                sx={theme => ({
                  flexDirection: "row",
                  gap: theme.spacing(4),

                  [theme.breakpoints.down("sm")]: {
                    flexDirection: "column"
                  }
                })}
              >
                <Box className="grow">
                  <Typography variant="subtitle2">
                    <Translate src="form:legend:user_form" />
                  </Typography>

                  <FormTextField
                    id="userName"
                    label={t("form:field:full_name")}
                    placeholder={t("form:placeholder:full_name")}
                    fieldControl={formHook.control}
                    fieldName="userName"
                    disabled
                  />

                  <FormTextField
                    id="userPhone"
                    label={t("form:field:phone")}
                    placeholder={t("form:placeholder:phone")}
                    fieldControl={formHook.control}
                    fieldName="userPhone"
                    onChange={phoneMask.onChange}
                  />
                </Box>

                <Box className="grow">
                  <Typography variant="subtitle2">
                    <Translate src="form:legend:access_form" />
                  </Typography>

                  <FormTextField
                    id="userEmail"
                    label={t("form:field:email")}
                    placeholder={t("form:placeholder:email")}
                    fieldControl={formHook.control}
                    fieldName="userEmail"
                    disabled
                  />

                  <FormTextFieldPassword
                    id="password"
                    label={t("form:field:password")}
                    placeholder={t("form:placeholder:password")}
                    fieldControl={formHook.control}
                    fieldName="password"
                    fieldType="password"
                  />

                  <FormTextFieldPassword
                    id="companyName"
                    label={t("form:field:password_confirm")}
                    placeholder={t("form:placeholder:password_confirm")}
                    fieldControl={formHook.control}
                    fieldName="confirmPassword"
                    fieldType="password"
                  />
                </Box>
              </Stack>

              <TermsOfUse fieldControl={formHook.control} />
              <ErrorMessage
                // eslint-disable-next-line no-underscore-dangle
                errors={formHook.formState.errors}
                name="termsAccepted"
                // eslint-disable-next-line react/no-unused-prop-types
                render={({ message }: { message: string }) => (
                  <Typography variant="body2" color="error">
                    {message}
                  </Typography>
                )}
              />
            </Box>
          </>
        }
      />
    </HashValidationWrapper>
  );
}
